/**
 * Settings Page
 *
 * User preferences management page. Currently supports language preferences
 * for article summaries and topic digests.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getUserPreferences, updateUserPreferences, getSupportedLanguages } from '../services/preferences';
import { getSourceSubscriptions, createSourceSubscription, deleteSourceSubscription } from '../services/feeds';
import { getApiKeys, createApiKey, revokeApiKey } from '../services/apiKeys';
import { useTheme } from '../hooks/useTheme';
import PageLoading from '../components/PageLoading';
import { setTitle } from '../utils/environment';

const Settings = () => {
  const [preferences, setPreferences] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null

  const { preference: themePref, setTheme, fontSize, setFontSize } = useTheme();

  // Source subscriptions state
  const [subscriptions, setSubscriptions] = useState([]);
  const [togglingSource, setTogglingSource] = useState(null);
  const [sourceError, setSourceError] = useState(null);

  // API key state
  const [apiKeys, setApiKeys] = useState([]);
  const [keyName, setKeyName] = useState('');
  const [keyScope, setKeyScope] = useState('general');
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState(null);
  const [keyError, setKeyError] = useState(null);
  // Reason: the server returns the raw key only at creation. It is held here so
  // the user can copy it, and is dropped on the next action — never refetched.
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Local form state
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [readableLanguages, setReadableLanguages] = useState(['en']);

  const fetchData = useCallback(async () => {
    try {
      const [prefsData, langsData, subsData] = await Promise.all([
        getUserPreferences(),
        getSupportedLanguages(),
        getSourceSubscriptions(),
      ]);
      setPreferences(prefsData);
      setLanguages(langsData.languages || []);
      setPreferredLanguage(prefsData.preferred_language || 'en');
      setReadableLanguages(prefsData.readable_languages || ['en']);
      setSubscriptions(subsData.subscriptions || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Reason: kept out of fetchData's Promise.all so a key-listing failure
  // degrades this one card instead of blanking the whole settings page.
  const fetchKeys = useCallback(async () => {
    try {
      setApiKeys(await getApiKeys());
      setKeyError(null);
    } catch (err) {
      setKeyError(err.message);
    }
  }, []);

  useEffect(() => {
    setTitle('Settings · Read It Never');
    fetchData();
    fetchKeys();
  }, [fetchData, fetchKeys]);

  const NEWS_SOURCES = [
    { source_type: 'hackernews', name: 'Hacker News', description: 'Top stories (min 100 points), polled every 30 min' },
    { source_type: 'github', name: 'GitHub Trending', description: 'Trending repositories, polled every 4 hours' },
  ];

  const API_KEY_SCOPES = [
    { value: 'general', name: 'General', description: 'The standard API surface' },
    { value: 'collector', name: 'Collector', description: 'WeChat collector intake only' },
    { value: 'wechat_feed', name: 'WeChat Feed', description: 'WeChat feed bridge context and ingest only' },
  ];

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!keyName.trim() || isCreatingKey) return;
    setIsCreatingKey(true);
    setKeyError(null);
    setNewKey(null);
    setCopied(false);
    try {
      const created = await createApiKey(keyName.trim(), keyScope);
      setNewKey(created);
      setKeyName('');
      await fetchKeys();
    } catch (err) {
      setKeyError(err.message);
    } finally {
      setIsCreatingKey(false);
    }
  };

  const handleCopyKey = async () => {
    if (!newKey?.key) return;
    try {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
    } catch {
      // Reason: clipboard access can be denied or unavailable over plain HTTP;
      // the key stays visible on screen, so selecting it by hand still works.
      setCopied(false);
      setKeyError('Could not copy automatically — select the key and copy it manually.');
    }
  };

  const handleRevokeKey = async (key) => {
    if (!window.confirm(`Revoke "${key.name}"? Anything using this key stops working immediately.`)) return;
    setRevokingKeyId(key.id);
    setKeyError(null);
    try {
      await revokeApiKey(key.id);
      if (newKey?.id === key.id) setNewKey(null);
      await fetchKeys();
    } catch (err) {
      setKeyError(err.message);
    } finally {
      setRevokingKeyId(null);
    }
  };

  const handleSourceToggle = async (source) => {
    setTogglingSource(source.source_type);
    setSourceError(null);
    const existing = subscriptions.find(s => s.source_type === source.source_type);
    try {
      if (existing) {
        await deleteSourceSubscription(existing.id);
        setSubscriptions(prev => prev.filter(s => s.id !== existing.id));
      } else {
        const created = await createSourceSubscription({
          source_type: source.source_type,
          name: source.name,
        });
        setSubscriptions(prev => [...prev, created]);
      }
    } catch (err) {
      setSourceError(err.message);
      setTimeout(() => setSourceError(null), 3000);
    } finally {
      setTogglingSource(null);
    }
  };

  // When preferred language changes, ensure it's in readable languages
  const handlePreferredChange = (newLang) => {
    setPreferredLanguage(newLang);
    if (!readableLanguages.includes(newLang)) {
      setReadableLanguages(prev => [newLang, ...prev]);
    }
    setSaveStatus(null);
  };

  const handleReadableToggle = (langCode) => {
    // Don't allow unchecking the preferred language
    if (langCode === preferredLanguage) return;

    setReadableLanguages(prev =>
      prev.includes(langCode)
        ? prev.filter(l => l !== langCode)
        : [...prev, langCode]
    );
    setSaveStatus(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      const updated = await updateUserPreferences({
        preferred_language: preferredLanguage,
        readable_languages: readableLanguages,
      });
      setPreferences(updated);
      setSaveStatus('success');
      // Auto-clear success after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      setSaveStatus('error');
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Check if form has changes
  const hasChanges = preferences && (
    preferredLanguage !== preferences.preferred_language ||
    JSON.stringify([...readableLanguages].sort()) !== JSON.stringify([...preferences.readable_languages].sort())
  );

  if (isLoading) {
    return (
      <PageLoading message="Loading settings..." />
    );
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface shadow-clay border-b-3 border-clay-border">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="mr-4 p-2 text-tertiary-text hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-clay transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-heading">Settings</h1>
                <p className="text-sm text-secondary-text mt-1">Manage your language preferences</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-clay text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Appearance Card */}
        <div className="card-clay-static p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-semibold text-heading mb-1">Appearance</h2>
          <p className="text-sm text-secondary-text mb-4">
            Choose how Rin looks to you.
          </p>
          <div className="inline-flex rounded-clay border-2 border-theme-border overflow-hidden">
            {[
              { value: 'system', label: 'System', icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )},
              { value: 'light', label: 'Light', icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )},
              { value: 'dark', label: 'Dark', icon: (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )},
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold transition-all duration-200
                  ${themePref === opt.value
                    ? 'bg-primary-500 text-white shadow-clay-sm'
                    : 'bg-surface text-secondary-text hover:text-heading hover:bg-surface-hover'
                  }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>

          {/* Font Size */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-body-text mb-2">Font Size</label>
            <p className="text-xs text-secondary-text mb-3">
              Adjust the text size across the app.
            </p>
            <div className="inline-flex rounded-clay border-2 border-theme-border overflow-hidden">
              {[
                { value: 'small', label: 'A', title: 'Small' },
                { value: 'default', label: 'A', title: 'Default' },
                { value: 'large', label: 'A', title: 'Large' },
                { value: 'x-large', label: 'A', title: 'Extra Large' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFontSize(opt.value)}
                  title={opt.title}
                  className={`flex items-center justify-center w-12 py-2 font-semibold transition-all duration-200
                    ${opt.value === 'small' ? 'text-xs' : opt.value === 'default' ? 'text-sm' : opt.value === 'large' ? 'text-base' : 'text-lg'}
                    ${fontSize === opt.value
                      ? 'bg-primary-500 text-white shadow-clay-sm'
                      : 'bg-surface text-secondary-text hover:text-heading hover:bg-surface-hover'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Language Preferences Card */}
        <div className="card-clay p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-semibold text-heading mb-1">Language Preferences</h2>
          <p className="text-sm text-secondary-text mb-6">
            Choose your preferred language for digests and how article summaries are handled.
          </p>

          {/* Preferred Language */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-body-text mb-2">
              Preferred Language
            </label>
            <p className="text-xs text-secondary-text mb-3">
              Topic digests and briefings will be generated in this language. Articles in
              languages you can&apos;t read will be summarized in this language too.
            </p>
            <select
              value={preferredLanguage}
              onChange={(e) => handlePreferredChange(e.target.value)}
              className="w-full sm:w-64 px-3 py-2 border border-theme-border-subtle rounded-clay shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                         text-sm bg-surface"
            >
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Readable Languages */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-body-text mb-2">
              Languages You Can Read
            </label>
            <p className="text-xs text-secondary-text mb-3">
              Articles in these languages will keep their original summary language.
              Articles in other languages will be translated to your preferred language.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {languages.map(lang => {
                const isPreferred = lang.code === preferredLanguage;
                const isChecked = readableLanguages.includes(lang.code);

                return (
                  <label
                    key={lang.code}
                    className={`flex items-center px-3 py-2.5 rounded-clay border transition-all duration-200 cursor-pointer
                      ${isChecked
                        ? 'bg-primary-50 dark:bg-primary-950/30 border-primary-200 dark:border-primary-800 shadow-sm'
                        : 'bg-surface border-theme-border-subtle hover:border-theme-border-subtle'
                      }
                      ${isPreferred ? 'opacity-80 cursor-default' : ''}
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleReadableToggle(lang.code)}
                      disabled={isPreferred}
                      className="h-4 w-4 text-primary-600 border-theme-border-subtle rounded focus:ring-primary-500"
                    />
                    <span className="ml-2.5 text-sm text-body-text">{lang.name}</span>
                    {isPreferred && (
                      <span className="ml-auto text-xs text-primary-500 font-medium">Preferred</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-4 pt-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className={`btn-clay px-6 py-2.5 text-sm font-medium rounded-clay transition-all duration-200
                ${hasChanges
                  ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-clay'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }
              `}
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Preferences'
              )}
            </button>

            {saveStatus === 'success' && (
              <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}

            {saveStatus === 'error' && (
              <span className="text-sm text-red-600 font-medium">
                Failed to save. Please try again.
              </span>
            )}
          </div>
        </div>

        {/* News Sources Card */}
        <div className="card-clay p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-semibold text-heading mb-1">News Sources</h2>
          <p className="text-sm text-secondary-text mb-6">
            Enable aggregated news sources for your daily briefings.
          </p>

          {sourceError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-clay text-red-700 dark:text-red-300 text-sm">
              {sourceError}
            </div>
          )}

          <div className="space-y-3">
            {NEWS_SOURCES.map((source) => {
              const enabled = subscriptions.some(s => s.source_type === source.source_type);
              const isToggling = togglingSource === source.source_type;
              return (
                <div key={source.source_type} className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-sm font-medium text-body-text">{source.name}</span>
                    <p className="text-xs text-secondary-text">{source.description}</p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={enabled}
                    disabled={isToggling}
                    onClick={() => handleSourceToggle(source)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                      ${enabled ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}
                      ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out mt-0.5
                        ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* API Keys Card */}
        <div className="card-clay p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-semibold text-heading mb-1">API Keys</h2>
          <p className="text-sm text-secondary-text mb-6">
            Create keys for tools that save articles into Rin on your behalf, such as the
            WeChat feed bridge. Up to 10 active keys.
          </p>

          {keyError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-clay text-red-700 dark:text-red-300 text-sm">
              {keyError}
            </div>
          )}

          {newKey && (
            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-clay">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
                Copy this key now — it is shown only once.
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mb-3">
                Rin stores only a hash. If you lose it, revoke the key and create another.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <code className="flex-1 px-3 py-2 bg-surface border border-theme-border-subtle rounded-clay text-xs font-mono break-all text-body-text">
                  {newKey.key}
                </code>
                <button
                  onClick={handleCopyKey}
                  className="btn-clay px-4 py-2 text-sm font-medium rounded-clay bg-primary-600 text-white hover:bg-primary-700 shadow-clay whitespace-nowrap"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleCreateKey} className="mb-6 flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="Key name (e.g. WeChat bridge)"
              maxLength={100}
              className="flex-1 px-3 py-2 border border-theme-border-subtle rounded-clay shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                         text-sm bg-surface"
            />
            <select
              value={keyScope}
              onChange={(e) => setKeyScope(e.target.value)}
              className="px-3 py-2 border border-theme-border-subtle rounded-clay shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                         text-sm bg-surface"
            >
              {API_KEY_SCOPES.map(scope => (
                <option key={scope.value} value={scope.value}>{scope.name}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={isCreatingKey || !keyName.trim()}
              className={`btn-clay px-6 py-2 text-sm font-medium rounded-clay transition-all duration-200 whitespace-nowrap
                ${isCreatingKey || !keyName.trim()
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 shadow-clay'
                }`}
            >
              {isCreatingKey ? 'Creating...' : 'Create Key'}
            </button>
          </form>

          <p className="text-xs text-secondary-text mb-4">
            {API_KEY_SCOPES.find(s => s.value === keyScope)?.description}
          </p>

          {apiKeys.length === 0 ? (
            <p className="text-sm text-secondary-text">No API keys yet.</p>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between gap-4 py-2 border-t border-theme-border-subtle pt-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-body-text truncate">{key.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-950/30 text-primary-600 dark:text-primary-300">
                        {API_KEY_SCOPES.find(s => s.value === key.scope)?.name || key.scope}
                      </span>
                    </div>
                    <p className="text-xs text-secondary-text font-mono mt-0.5">{key.key_prefix}…</p>
                    <p className="text-xs text-tertiary-text mt-0.5">
                      Created {new Date(key.created_at).toLocaleDateString()}
                      {key.last_used_at
                        ? ` · last used ${new Date(key.last_used_at).toLocaleDateString()}`
                        : ' · never used'}
                      {key.expires_at ? ` · expires ${new Date(key.expires_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRevokeKey(key)}
                    disabled={revokingKeyId === key.id}
                    className="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
                  >
                    {revokingKeyId === key.id ? 'Revoking...' : 'Revoke'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Section */}
        <div className="card-clay p-6 sm:p-8 mb-6">
          <h2 className="text-lg font-semibold text-heading mb-4">Admin</h2>
          <div className="space-y-1">
            {[
              { to: '/admin/queue', icon: '📊', label: 'Processing Queue' },
              { to: '/admin/costs', icon: '💰', label: 'LLM Costs' },
              { to: '/admin/pricing', icon: '💲', label: 'LLM Pricing' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between px-3 py-3 rounded-clay hover:bg-clay-bg transition-colors group"
              >
                <span className="flex items-center text-sm text-body-text">
                  <span className="mr-3">{item.icon}</span>
                  {item.label}
                </span>
                <svg className="w-4 h-4 text-tertiary-text group-hover:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </div>

        {/* Explanation Card */}
        <div className="mt-6 card-clay p-6 sm:p-8 bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/40">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">How it works</h3>
          <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1.5">
            <li><strong>Topic Digests &amp; Briefings</strong> &mdash; Always generated in your preferred language.</li>
            <li><strong>Article Summaries</strong> &mdash; If the article is in a language you can read, the summary stays in that language. Otherwise, it&apos;s translated to your preferred language.</li>
            <li><strong>No preferences set?</strong> &mdash; Everything works as before: summaries match the article language, digests auto-detect.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Settings;
